import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { AppShell } from '../components/app-shell/AppShell';
import { SettingsTabs } from '../components/app-shell/SettingsTabs';
import { EmptyState, MotionSection, PageHero } from '../components/ui';
import { fetchOk } from '../lib/api';
import { csrfHeaders, useAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';
import { useStripeSubscriptionSync } from '../lib/venue-subscription';

type SubscriptionDto = {
  id: string;
  state: 'trial' | 'active' | 'past_due' | 'paused' | 'cancelled';
  tier: 'founding' | 'standard' | 'premium';
  billing_cycle: 'monthly' | 'annual';
  price_amount_minor: number;
  price_currency: string;
  trial_started_at: number | null;
  trial_ends_at: number | null;
  trial_days_remaining: number | null;
  current_period_start: number | null;
  current_period_end: number | null;
  next_charge_at: number | null;
  last_charge_at: number | null;
  grandfathered_pricing: boolean;
  cancelled_at: number | null;
  cancellation_reason: string | null;
  /** Stripe will end the subscription then (a scheduled cancellation). */
  cancel_at: number | null;
};

type BillingMeResponse = {
  ok: boolean;
  account: { id: string; display_name: string; currency: string } | null;
  subscription: SubscriptionDto | null;
  is_founding_cohort?: boolean;
  /** Account-level founding-host flag (CFR-015). True when the host has any
   *  founding venue or has been backfilled by migration 0005. Drives the
   *  "Founding host plan — locked for life" framing on the plan card. */
  is_founding?: boolean;
  /** Monthly price in AUD majors for the founding rate. Surfaced so the
   *  plan card can show the locked-for-life price without doing minor-unit
   *  math against a possibly-stub subscription. */
  monthly_price_aud?: number;
  /** True when this account can use real Stripe Checkout + the billing portal
   *  (`stripe_live`, or an account created through a secret preview link). */
  stripe_live?: boolean;
};

type InvoiceLine = {
  id: string;
  period_start: number;
  period_end: number;
  amount_minor: number;
  currency: string;
  description: string;
  paid: boolean;
  paid_at: number | null;
};

const ME_KEY = ['billing', 'me'] as const;
const INVOICES_KEY = ['billing', 'invoices'] as const;

type BillingSearch = { checkout?: 'success' | 'cancelled' };

export const Route = createFileRoute('/billing')({
  // `?checkout=success|cancelled` — Stripe Checkout's return URLs
  // (worker routes/billing.ts startVenueCheckout).
  validateSearch: (s: Record<string, unknown>): BillingSearch =>
    s.checkout === 'success' || s.checkout === 'cancelled' ? { checkout: s.checkout } : {},
  component: BillingPage,
});

/** POST a billing endpoint that answers with a Stripe-hosted URL, then go there. */
async function redirectToStripe(endpoint: string, urlField: 'checkoutUrl' | 'url'): Promise<void> {
  // fetchOk surfaces the server's message whether it answered JSON or text.
  const res = await fetchOk(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify({}),
  });
  const url = ((await res.json().catch(() => ({}))) as Record<string, unknown>)[urlField];
  if (typeof url !== 'string') {
    throw new Error('Billing is unavailable right now. Try again shortly.');
  }
  window.location.assign(url);
}

function BillingPage() {
  const queryClient = useQueryClient();
  const { capabilities } = useAuth();
  // Billing is host/admin-only. Without this gate, artist-only sessions fire
  // /api/billing/{me,invoices} unconditionally and the Worker returns 403 —
  // a confusing cascade in the console for what is really a "wrong audience"
  // page hit. Render a friendly empty state for non-hosts instead.
  const canSeeBilling = !!capabilities && (capabilities.isHost || capabilities.isAdmin);
  const { checkout: checkoutReturn } = Route.useSearch();

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<BillingMeResponse> => {
      const res = await fetch('/api/billing/me', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load billing');
      return res.json();
    },
    enabled: canSeeBilling,
    // Back from a paid Checkout, the subscription row is created by the sync
    // below a few seconds after the browser lands — poll until it shows.
    refetchInterval: (q) =>
      checkoutReturn === 'success' && q.state.data?.subscription == null ? 3000 : false,
  });
  // Ask Stripe directly while waiting, rather than wait for the 15-minute event poll.
  useStripeSubscriptionSync(canSeeBilling && checkoutReturn === 'success' && me.data?.subscription == null);

  const invoices = useQuery({
    queryKey: INVOICES_KEY,
    queryFn: async (): Promise<{ items: InvoiceLine[] }> => {
      const res = await fetch('/api/billing/invoices', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load invoices');
      return res.json();
    },
    enabled: canSeeBilling,
  });

  const simulate = useMutation({
    mutationFn: async (event: 'trial_end' | 'charge_succeeded' | 'charge_failed' | 'cancel') => {
      const res = await fetch('/api/billing/__dev__/simulate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ event }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? 'Simulation failed');
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ME_KEY }),
        queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
      ]);
      showToast({ kind: 'success', body: 'Subscription updated.' });
    },
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Failed' }),
  });

  const startCheckout = useMutation({
    mutationFn: () => redirectToStripe('/api/billing/checkout', 'checkoutUrl'),
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Failed' }),
  });

  const openPortal = useMutation({
    mutationFn: () => redirectToStripe('/api/billing/portal', 'url'),
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Failed' }),
  });

  // FR-008: the production cancel button drives the REAL endpoint, not the
  // dev-only simulator (which 404s outside development). Cancellation is
  // immediate per the Subscription state machine's owner.cancel transition.
  const cancel = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/billing/cancel', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? 'Could not cancel your subscription.');
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ME_KEY }),
        queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
      ]);
      showToast({ kind: 'success', body: 'Your subscription has been cancelled.' });
    },
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Failed' }),
  });

  if (!canSeeBilling) {
    return (
      <AppShell tone="form">
        <EmptyState
          title="Billing lives on the host side"
          body="Subscriptions and invoices are part of the host account. If you host artists, add a venue from Settings → Account to set up billing."
        />
      </AppShell>
    );
  }

  if (me.isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (me.error) {
    return (
      <AppShell tone="form">
        <Text c="red">We couldn’t load your subscription. Try again shortly.</Text>
      </AppShell>
    );
  }

  const sub = me.data?.subscription;
  const account = me.data?.account;
  const items = invoices.data?.items ?? [];
  const stripeLive = me.data?.stripe_live === true;

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Billing"
            title="Your subscription"
            lede={`${account?.display_name ?? 'Your venue account'} · all amounts in ${account?.currency ?? 'AUD'}.`}
            action={
              <Button variant="subtle" size="sm" onClick={() => window.history.back()}>
                ← Back
              </Button>
            }
          />
        </MotionSection>

        <SettingsTabs current="billing" />

        {checkoutReturn === 'success' ? (
          <Alert variant="light" color="teal">
            {sub
              ? 'Payment received. Your subscription is set up.'
              : 'Payment received. Your subscription will appear here in a few seconds.'}
          </Alert>
        ) : checkoutReturn === 'cancelled' ? (
          <Alert variant="light" color="gray">
            Checkout was cancelled. Nothing was charged.
          </Alert>
        ) : null}

        {!sub ? (
          <Card withBorder padding="lg">
            {stripeLive ? (
              <Stack gap="sm" align="flex-start">
                <Text>
                  No subscription on file yet. Start one to keep your venue listed once it
                  goes live. You&apos;ll pay securely through Stripe.
                </Text>
                <Button
                  onClick={() => startCheckout.mutate()}
                  loading={startCheckout.isPending}
                  disabled={checkoutReturn === 'success'}
                >
                  Start subscription
                </Button>
              </Stack>
            ) : (
              <Text>
                No subscription on file yet. Once payments are wired your trial will
                begin automatically when you publish your first venue.
              </Text>
            )}
            {import.meta.env.DEV && (
              <Stack
                mt="md"
                p="md"
                gap="xs"
                style={{
                  border: '1px dashed var(--mantine-color-sun-3)',
                  borderRadius: 6,
                }}
              >
                <Text size="xs" c="sun.7" tt="uppercase" fw={600}>
                  Dev only. Not visible in production.
                </Text>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => simulate.mutate('trial_end')}
                  loading={simulate.isPending}
                >
                  Start trial (dev simulator)
                </Button>
              </Stack>
            )}
          </Card>
        ) : (
          <>
            <PlanCard
              sub={sub}
              isFounding={me.data?.is_founding === true}
              monthlyPriceAud={me.data?.monthly_price_aud ?? null}
            />

            <Card withBorder padding="lg">
              <Stack gap="xs">
                <Text fw={600}>Payment method</Text>
                {stripeLive ? (
                  <Group justify="space-between" align="center">
                    <Text c="dimmed" size="sm">
                      Update your card, download receipts or cancel in Stripe.
                    </Text>
                    <Button
                      variant="light"
                      onClick={() => openPortal.mutate()}
                      loading={openPortal.isPending}
                    >
                      Manage billing
                    </Button>
                  </Group>
                ) : (
                  <Text c="dimmed" size="sm">
                    Payments coming soon. Your trial continues.
                  </Text>
                )}
              </Stack>
            </Card>

            <InvoicesCard items={items} />

            <PauseCancelCard
              sub={sub}
              managedInStripe={stripeLive}
              onSimulate={(e) => simulate.mutate(e)}
              onCancel={() => {
                if (
                  window.confirm(
                    'Cancel your subscription now? This takes effect immediately.',
                  )
                ) {
                  cancel.mutate();
                }
              }}
              busy={simulate.isPending}
              cancelBusy={cancel.isPending}
            />
          </>
        )}
      </Stack>
    </AppShell>
  );
}

function PlanCard({
  sub,
  isFounding,
  monthlyPriceAud,
}: {
  sub: SubscriptionDto;
  isFounding: boolean;
  monthlyPriceAud: number | null;
}) {
  // CFR-015: when the account is in the founding cohort, lead with the
  // founding-host framing that earned the conversion on /founding-venues.
  // Otherwise fall back to the generic tier label.
  const planHeading = isFounding
    ? 'Founding host plan'
    : sub.tier === 'premium'
      ? 'Premium plan'
      : 'Standard plan';
  const planSubtitle = isFounding
    ? `$${(monthlyPriceAud ?? sub.price_amount_minor / 100).toFixed(0)} AUD/month, locked for life.`
    : sub.tier === 'founding'
      ? 'Founding Venue (lifetime grandfathered rate)'
      : null;

  const stateBadge =
    sub.state === 'trial'
      ? { color: 'blue', label: 'Trial' }
      : sub.state === 'active'
        ? { color: 'teal', label: 'Active' }
        : sub.state === 'past_due'
          ? { color: 'yellow', label: 'Past due' }
          : sub.state === 'paused'
            ? { color: 'gray', label: 'Paused' }
            : { color: 'red', label: 'Cancelled' };

  const price = formatMoney(sub.price_amount_minor, sub.price_currency);

  return (
    <Card withBorder padding="lg">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>Plan</Text>
          <Badge color={stateBadge.color} variant="light">
            {stateBadge.label}
          </Badge>
        </Group>
        <Text fw={600} size="lg">
          {planHeading}
        </Text>
        {planSubtitle !== null ? <Text c="dimmed">{planSubtitle}</Text> : null}

        {isFounding ? (
          <Alert variant="light" color="sun">
            You’re in the founding cohort. The standard plan won’t apply to your account.
          </Alert>
        ) : null}

        {sub.state === 'trial' && sub.trial_days_remaining !== null ? (
          <Alert variant="light" color="blue">
            {sub.trial_days_remaining} days of trial left. We’ll let you know before
            anything changes.
          </Alert>
        ) : null}

        {sub.cancel_at !== null && sub.state !== 'cancelled' ? (
          <Alert variant="light" color="sun">
            Your subscription ends on {formatDate(sub.cancel_at)}, and you won&apos;t be charged again. Changed your
            mind? Use Manage billing to keep it.
          </Alert>
        ) : sub.state === 'active' && sub.next_charge_at !== null ? (
          <Text c="dimmed" size="sm">
            {price} a month, charged on the 1st. Next charge: {formatDate(sub.next_charge_at)}.
          </Text>
        ) : null}

        {sub.state === 'past_due' ? (
          <Alert variant="light" color="yellow">
            Last charge didn’t go through. Once payments are wired we’ll retry; in the
            meantime your venues stay listed.
          </Alert>
        ) : null}

        {sub.state === 'cancelled' ? (
          <Text c="dimmed" size="sm">
            Cancelled {sub.cancelled_at !== null ? `on ${formatDate(sub.cancelled_at)}` : ''}.
            {sub.cancellation_reason ? ` Reason: ${sub.cancellation_reason}` : ''}
          </Text>
        ) : null}

        <Divider my="xs" />
        <Text size="sm" c="dimmed">
          After trial: {price} / {sub.billing_cycle}
        </Text>
        {/* CFR-015: annual billing isn't wired through Stripe — first cohort
            sets it up by hand via reply-to-email. The link points at the
            founder-mediated path rather than a self-serve switch. */}
        <Text size="sm" c="dimmed">
          Annual billing. Pay for 10 months, get a full year. Reply to your subscription
          email and we&apos;ll set it up.
        </Text>
      </Stack>
    </Card>
  );
}

function InvoicesCard({ items }: { items: InvoiceLine[] }) {
  return (
    <Card withBorder padding="lg">
      <Text fw={600} mb="xs">
        Invoices
      </Text>
      {items.length === 0 ? (
        <Text c="dimmed" size="sm">
          No invoices yet. Your trial is still active.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={480}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Period</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td>
                    {formatDate(i.period_start)} – {formatDate(i.period_end)}
                  </Table.Td>
                  <Table.Td>{i.description}</Table.Td>
                  <Table.Td>{formatMoney(i.amount_minor, i.currency)}</Table.Td>
                  <Table.Td>
                    {i.paid ? (
                      <Badge color="teal" variant="light">
                        Paid
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        Unpaid
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Card>
  );
}

function PauseCancelCard({
  sub,
  managedInStripe,
  onSimulate,
  onCancel,
  busy,
  cancelBusy,
}: {
  sub: SubscriptionDto;
  /** Stripe-billed: cancellation goes through the Stripe portal, because the
   *  in-app cancel only changes our row and would leave Stripe charging. */
  managedInStripe: boolean;
  onSimulate: (e: 'trial_end' | 'charge_succeeded' | 'charge_failed' | 'cancel') => void;
  onCancel: () => void;
  busy: boolean;
  cancelBusy: boolean;
}) {
  // Cancel is a real user affordance and stays in the production UI — it drives
  // the real /api/billing/cancel route (FR-008). The "Simulate ..." buttons
  // drive /api/billing/__dev__/simulate and must NEVER ship to production — Vite
  // tree-shakes the dev block in production builds because `import.meta.env.DEV`
  // is the literal `false`. (See WW-FUNC-212.)
  return (
    <Card withBorder padding="lg">
      <Stack gap="xs">
        <Text fw={600}>Pause or cancel</Text>
        <Text size="sm" c="dimmed">
          Pause keeps your seat at the founding rate; cancel ends the subscription
          immediately.
        </Text>
        {managedInStripe ? (
          <Text size="sm">
            To cancel, use Manage billing above. Your subscription here updates once
            Stripe confirms it.
          </Text>
        ) : (
          <Group mt="xs">
            <Button
              color="red"
              variant="outline"
              onClick={onCancel}
              disabled={sub.state === 'cancelled'}
              loading={cancelBusy}
            >
              Cancel subscription
            </Button>
          </Group>
        )}

        {import.meta.env.DEV && (
          <Stack
            mt="md"
            p="md"
            gap="xs"
            style={{
              border: '1px dashed var(--mantine-color-sun-3)',
              borderRadius: 6,
            }}
          >
            <Text size="xs" c="sun.7" tt="uppercase" fw={600}>
              Dev only. Not visible in production.
            </Text>
            <Text size="xs" c="dimmed">
              Drives the Subscription state machine via /api/billing/__dev__/simulate
              while real Stripe wiring is stubbed.
            </Text>
            <Group>
              <Button
                size="xs"
                variant="default"
                disabled={sub.state !== 'active'}
                onClick={() => onSimulate('charge_failed')}
                loading={busy}
              >
                Simulate failed charge
              </Button>
              {sub.state === 'trial' ? (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => onSimulate('trial_end')}
                  loading={busy}
                >
                  Simulate trial end
                </Button>
              ) : null}
              {sub.state === 'active' ? (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => onSimulate('charge_succeeded')}
                  loading={busy}
                >
                  Simulate monthly charge
                </Button>
              ) : null}
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function formatMoney(minor: number, currency: string): string {
  const major = minor / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(major);
}

// Billing runs on Brisbane calendar months; render in Brisbane time so a
// charge at 00:00 on the 1st never reads as the 31st from another timezone.
function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
